import { useState, useMemo } from 'react'
import { format, subDays, startOfWeek, startOfMonth, isAfter, parseISO, eachDayOfInterval, addDays } from 'date-fns'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Legend,
} from 'recharts'
import { CalendarDays, AlertTriangle, Inbox } from 'lucide-react'
import { useIsMobile } from '@/hooks/useIsMobile'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Spinner } from '@/components/ui/spinner'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useFunnelStats } from '@/hooks/useStats'
import { ProjectSize } from '@/types/api'
import { SalesFunnelChart } from '@/components/SalesFunnelChart'
import { WeeklyComparisonChart } from '@/components/WeeklyComparisonChart'

const SOURCE_COLORS = [
  '#10B981',
  '#3B82F6',
  '#F59E0B',
  '#8B5CF6',
  '#EC4899',
  '#06B6D4',
  '#F97316',
  '#84CC16',
  '#6366F1',
  '#14B8A6',
]

const sourceLabels: Record<string, string> = {
  phone: 'Phone',
  walk_in: 'Walk-in',
  website: 'Website',
  referral: 'Referral',
  google_ads: 'Google Ads',
  facebook: 'Facebook',
  instagram: 'Instagram',
  yelp: 'Yelp',
  recurring_client: 'Recurring Client',
  other: 'Other',
}

const SIZE_ORDER = Object.values(ProjectSize)

const dateRangeOptions = [
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: '365', label: 'Last year' },
  { value: 'all', label: 'All time' },
]

export function ReportsPage() {
  const [dateRange, setDateRange] = useState('30')
  const [groupBy, setGroupBy] = useState<'day' | 'week' | 'month'>('day')
  const isMobile = useIsMobile()

  // Fetch ALL data once - no date filtering on API call
  const { data: stats, isLoading, isError } = useFunnelStats()

  // Filter and transform data in frontend based on selected date range
  const filteredData = useMemo(() => {
    if (!stats) return null

    // Calculate the cutoff date for filtering
    const now = new Date()
    const cutoffDate = dateRange === 'all'
      ? null
      : subDays(now, parseInt(dateRange))

    // Filter leads_by_date based on selected range
    const filteredLeadsByDate = cutoffDate
      ? stats.leads_by_date.filter((item) => {
          const itemDate = parseISO(item.date)
          return isAfter(itemDate, cutoffDate)
        })
      : stats.leads_by_date

    // Fill missing dates with 0 so the chart shows a continuous timeline
    const filledLeadsByDate = fillMissingDates(filteredLeadsByDate, cutoffDate)

    // Group timeline data by day/week/month
    const groupedTimeline = groupTimelineData(filledLeadsByDate, groupBy)

    // For the summary cards, we use the API totals (all time)
    // since we can't recalculate status counts from leads_by_date
    return {
      total_leads: stats.total_leads,
      total_won: stats.total_won,
      total_lost: stats.total_lost,
      conversion_rate: stats.conversion_rate,
      count_by_status: stats.count_by_status,
      count_by_source: stats.count_by_source,
      count_by_size: stats.count_by_size,
      leads_by_date: groupedTimeline,
      count_by_project_status: stats.count_by_project_status,
      total_projects: stats.total_projects,
      total_projects_won: stats.total_projects_won,
      total_projects_lost: stats.total_projects_lost,
    }
  }, [stats, dateRange, groupBy])

  // Expand a sparse date array to include every date in the range, defaulting missing days to 0
  function fillMissingDates(
    data: { date: string; count: number }[],
    cutoffDate: Date | null
  ): { date: string; count: number }[] {
    const today = new Date()
    const startDate = cutoffDate
      ? addDays(cutoffDate, 1)
      : data.length > 0
        ? parseISO(data[0].date)
        : today

    if (startDate > today) return data

    const dateMap = new Map(data.map((item) => [item.date, item.count]))
    return eachDayOfInterval({ start: startDate, end: today }).map((day) => {
      const key = format(day, 'yyyy-MM-dd')
      return { date: key, count: dateMap.get(key) ?? 0 }
    })
  }

  // Group timeline data by day, week, or month
  function groupTimelineData(
    data: { date: string; count: number }[],
    grouping: 'day' | 'week' | 'month'
  ): { date: string; count: number }[] {
    if (grouping === 'day') {
      return data
    }

    const grouped = new Map<string, number>()

    data.forEach((item) => {
      const itemDate = parseISO(item.date)
      let key: string

      if (grouping === 'week') {
        const weekStart = startOfWeek(itemDate, { weekStartsOn: 1 })
        key = format(weekStart, 'yyyy-MM-dd')
      } else {
        const monthStart = startOfMonth(itemDate)
        key = format(monthStart, 'yyyy-MM-dd')
      }

      grouped.set(key, (grouped.get(key) || 0) + item.count)
    })

    return Array.from(grouped.entries())
      .map(([date, count]) => ({ date, count }))
      .sort((a, b) => a.date.localeCompare(b.date))
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner size="lg" />
      </div>
    )
  }

  // Error state
  if (isError) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-4">
        <AlertTriangle className="h-12 w-12 text-warning" />
        <p className="text-lg text-muted-foreground">There are some issues in the kitchen</p>
      </div>
    )
  }

  // Empty state
  if (!filteredData || filteredData.total_leads === 0) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-4">
        <Inbox className="h-12 w-12 text-muted-foreground" />
        <p className="text-lg text-muted-foreground">There is nothing here yet!</p>
      </div>
    )
  }

  // Prepare source pie chart data
  const sourceData =
    filteredData.count_by_source.map((item, index) => ({
      name: sourceLabels[item.source] || item.source,
      value: item.count,
      fill: SOURCE_COLORS[index % SOURCE_COLORS.length],
    }))

  // Prepare timeline data with proper date formatting
  const timelineData =
    filteredData.leads_by_date.map((item) => ({
      date: groupBy === 'month'
        ? format(parseISO(item.date), 'MMM yyyy')
        : format(parseISO(item.date), 'MMM d'),
      leads: item.count,
    }))

  const hasProjectStats = filteredData.count_by_project_status && filteredData.count_by_project_status.length > 0

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Reports</h1>
          <p className="text-muted-foreground">
            Analyze your sales funnel and lead metrics
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <CalendarDays className="h-4 w-4" />
            Date Range
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            <Select value={dateRange} onValueChange={setDateRange}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Select range" />
              </SelectTrigger>
              <SelectContent>
                {dateRangeOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={groupBy} onValueChange={(v) => setGroupBy(v as typeof groupBy)}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Group by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="day">Daily</SelectItem>
                <SelectItem value="week">Weekly</SelectItem>
                <SelectItem value="month">Monthly</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Metrics Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Leads
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-3xl font-bold">{filteredData.total_leads}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Won Deals
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-3xl font-bold text-success">
              {filteredData.total_projects_won ?? filteredData.total_won}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Lost Deals
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-3xl font-bold text-error">
              {filteredData.total_projects_lost ?? filteredData.total_lost}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Conversion Rate
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-3xl font-bold text-primary">
              {(filteredData.conversion_rate).toFixed(1)}%
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Funnel Chart - uses project stats if available */}
        <Card>
          <CardHeader>
            <CardTitle>Sales Funnel</CardTitle>
            <CardDescription>
              {hasProjectStats ? 'Project distribution by status' : 'Lead distribution by status'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <SalesFunnelChart
              countByStatus={filteredData.count_by_status}
              totalLeads={filteredData.total_leads}
              countByProjectStatus={filteredData.count_by_project_status ?? undefined}
              totalProjects={filteredData.total_projects ?? undefined}
              height={300}
            />
          </CardContent>
        </Card>

        {/* Source Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Lead Sources</CardTitle>
            <CardDescription>Where your leads come from</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={sourceData}
                    cx="50%"
                    cy="50%"
                    labelLine={!isMobile}
                    label={isMobile ? false : ({ name, percent }) =>
                      `${name} (${(percent * 100).toFixed(0)}%)`
                    }
                    outerRadius={80}
                    dataKey="value"
                  >
                    {sourceData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip />
                  {isMobile && <Legend />}
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Weekly Comparison Chart */}
      <WeeklyComparisonChart leadsByDate={stats!.leads_by_date} />

      {/* Timeline Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Leads Over Time</CardTitle>
          <CardDescription>New leads created over the selected period</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={timelineData}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  angle={isMobile ? -45 : 0}
                  textAnchor={isMobile ? 'end' : 'middle'}
                  interval="preserveStartEnd"
                  height={isMobile ? 60 : 30}
                />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="leads"
                  name="New Leads"
                  stroke="#10B981"
                  strokeWidth={2}
                  dot={{ fill: '#10B981' }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Project Size Distribution */}
      {filteredData.count_by_size && filteredData.count_by_size.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Project Size Distribution</CardTitle>
            <CardDescription>Won deals by project size</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={[...filteredData.count_by_size]
                    .sort((a, b) => SIZE_ORDER.indexOf(a.size as ProjectSize) - SIZE_ORDER.indexOf(b.size as ProjectSize))
                    .map((item) => ({
                      name: item.size.charAt(0).toUpperCase() + item.size.slice(1),
                      count: item.count,
                    }))}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" name="Won Deals" fill="#10B981" barSize={36} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
